import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import get from 'lodash/get';
import { Labeled, useInput } from 'react-admin';

import { formatBytes } from '@src/utils/number-helpers';

import ExpirationDate from '@src/custom/expiration-date';
import CopyURLButton from './copy-image-url-button';
import EditButton from './edit-image-button';
import ImageChooserButton from './image_chooser_button';
import ImageUploaderButton from './image_uploader_button';
import RemoveButton from './remove-image-button';

const StyledDetails = styled('div')({
    display: 'flex',
    flexDirection: 'column',
});

const StyledContent = styled(CardContent)({
    flex: '1 0 auto',
});

const StyledThumbnailContainer = styled('div')({
    position: 'relative',
    background: `
        linear-gradient(45deg, #f5f5f5 25%, transparent 25%),
        linear-gradient(-45deg, #f5f5f5 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #f5f5f5 75%),
        linear-gradient(-45deg, transparent 75%, #f5f5f5 75%)
    `,
    backgroundSize: '16px 16px',
    backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
});

const StyledThumbnail = styled(CardMedia)({
    height: '100%',
    width: '100%',
    minHeight: '14rem',
});

const StyledActions = styled(CardActions)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    paddingLeft: typeof theme?.spacing === 'function' ? theme.spacing(1) : '8px',
    paddingBottom: typeof theme?.spacing === 'function' ? theme.spacing(1) : '8px',
}));

const ImagePreview = ({ field }) => {
    if (!field?.value) return null;

    return (
        <StyledThumbnailContainer as={Grid} size={{ xs: 12, sm: 3, md: 4 }}>
            <StyledThumbnail
                image={get(field.value, 'source')}
                title={get(field.value, 'alt')}
            />
        </StyledThumbnailContainer>
    );
};

const ImageDetails = ({
    field,
    hasImageMeta,
    folderName,
    allowEdit,
    handleChooserChoice,
    handleImageUploaded,
    handleImageUpdated,
    handleImageRemoved,
}) => (
    <StyledDetails as={Grid} size={{ xs: 12, sm: 9, md: 8 }}>
        <StyledContent>
            <Typography component="h5" variant="h5">
                { field?.value ? get(field.value, 'alt') : '' }
            </Typography>
            <ExpirationDate expiredAt={field?.value ? get(field.value, 'expired_at') : null} />
            {
                hasImageMeta ?
                    <Typography variant="body2" color="textSecondary">
                        {
                            [
                                get(field.value, 'width') && get(field.value, 'height') ? `${get(field.value, 'width')}x${get(field.value, 'height')}px` : null,
                                get(field.value, 'bytes') ? formatBytes(get(field.value, 'bytes')) : null,
                            ].filter(Boolean).join(', ')
                        }
                    </Typography> :
                    null
            }
        </StyledContent>
        <StyledActions>
            <ImageChooserButton folderName={folderName} onImageChoosed={handleChooserChoice} />
            <ImageUploaderButton folderName={folderName} onImageUploaded={handleImageUploaded} />
        </StyledActions>
        {
            field?.value ?
                <StyledActions>
                    <CopyURLButton image={field.value} />
                    {
                        allowEdit ?
                            <>
                                <EditButton image={field.value} onUpdated={handleImageUpdated} />
                                <RemoveButton
                                    image={field.value}
                                    onRemove={handleImageRemoved}
                                />
                            </> :
                            null
                    }
                </StyledActions> :
                null
        }
    </StyledDetails>
);

const isInputRequired = (useInputIsRequired, props) => {
    return Boolean(useInputIsRequired || props.isRequired || props.required);
};

const getFieldMeta = (fieldState, isSubmitted) => {
    if (!fieldState) return null;

    const error = fieldState.error?.message || fieldState.error;
    const errorMessage = typeof error === 'string' ? error : error?.message;

    return {
        touched: fieldState.isTouched,
        error: typeof errorMessage === 'string' ? errorMessage : (errorMessage ? JSON.stringify(errorMessage) : null),
        invalid: fieldState.invalid,
        isSubmitted,
    };
};

const logMissingInputOrMeta = (props, input, meta, isRequired) => {
    if (!input || !meta) {
        console.error('ImageInput: input or meta is undefined', {
            props,
            input,
            meta,
            isRequired,
            hasSource: Boolean(props.source),
            hasResource: Boolean(props.resource),
            sourceValue: props.source,
            resourceValue: props.resource,
        });
    }
};

const checkHasImageMeta = (field) => {
    if (!field || !field.value) return false;
    const { width, height, bytes } = field.value;
    return Boolean(width || height || bytes);
};

const shouldShowError = (meta) => {
    if (!meta) return false;
    return Boolean((meta.touched || meta.isSubmitted) && meta.error);
};

const ImageInput = ({
    fullWidth = true,
    helperText,
    label = 'Featured Image',
    folderName = 'featured',
    allowEdit = true,
    ...props
}) => {
    const {
        field,
        fieldState,
        formState,
        isRequired: useInputIsRequired,
    } = useInput(props);

    const isRequired = isInputRequired(useInputIsRequired, props);
    const isSubmitted = formState?.isSubmitted;

    const input = useMemo(() => field ? {
        value: field.value,
        onChange: field.onChange,
        onBlur: field.onBlur,
        name: field.name,
    } : null, [field]);

    const meta = useMemo(() => getFieldMeta(fieldState, isSubmitted), [fieldState, isSubmitted]);

    // Debug logging to help identify the issue
    logMissingInputOrMeta(props, input, meta, isRequired);

    const handleChooserChoice = useCallback((image) => {
        if (!field) return;
        field.onChange(image);
    }, [field]);

    const handleImageUploaded = useCallback((image) => {
        if (!field) return;
        field.onChange(image);
    }, [field]);

    const handleImageUpdated = useCallback((image) => {
        if (!field) return;
        field.onChange(image);
        field.onBlur();
    }, [field]);

    const handleImageRemoved = useCallback(() => {
        if (!field) return;
        field.onChange(null);
        field.onBlur();
    }, [field]);

    const hasImageMeta = checkHasImageMeta(field);
    const showError = shouldShowError(meta);

    return (
        <Labeled
            fullWidth={fullWidth}
            label={label}
            isRequired={isRequired}
            meta={meta || {}}
            input={input || {}}
            resource={props.resource}
            source={props.source}
        >
            <Grid container sx={{ width: '100%' }} spacing={0}>
                <Grid size={12}>
                    <Card variant="outlined">
                        <Grid container sx={{ width: '100%' }} spacing={0}>
                            <ImagePreview field={field} />
                            <ImageDetails
                                allowEdit={allowEdit}
                                field={field}
                                folderName={folderName}
                                handleChooserChoice={handleChooserChoice}
                                handleImageRemoved={handleImageRemoved}
                                handleImageUpdated={handleImageUpdated}
                                handleImageUploaded={handleImageUploaded}
                                hasImageMeta={hasImageMeta}
                            />
                        </Grid>
                    </Card>
                </Grid>
                { showError ? <Grid size={12}><FormHelperText error>{meta.error}</FormHelperText></Grid> : null }
                { helperText ? <Grid size={12}><FormHelperText>{helperText}</FormHelperText></Grid> : null }
            </Grid>
        </Labeled>
    );
};

ImageInput.propTypes = {
    fullWidth: PropTypes.bool,
    helperText: PropTypes.string,
    isRequired: PropTypes.bool,
    label: PropTypes.string,
    resource: PropTypes.string,
    source: PropTypes.string,
    folderName: PropTypes.string,
    required: PropTypes.bool,
    allowEdit: PropTypes.bool,
};

export default React.memo(ImageInput);
